import { QueryKey, useMutation, useQuery, useQueryCache } from "react-query";
import {
  createMapLayer,
  deactivateMapLayer,
  getMapLayer,
  getMapLayers,
  reactivateMapLayer,
  updateMapLayer,
} from "../queries/maps";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

export const MAP_LAYERS_LIST_QUERY_KEY = "maplayer" as const;
export function useMapLayers() {
  const queryKey = MAP_LAYERS_LIST_QUERY_KEY;

  const networkHeaders = useNetworkHeaders();
  const mapLayersQuery = useQuery(queryKey, getMapLayers(networkHeaders));

  return { mapLayersQuery, queryKey };
}

export function useMapLayer(id?: number | string) {
  const queryKey = getMapLayerQueryKey(id || 0);

  const networkHeaders = useNetworkHeaders();
  const layerQuery = useQuery(queryKey, () => getMapLayer(networkHeaders)(id!), { enabled: id !== undefined });

  return { layerQuery, queryKey };
}

export function getMapLayerQueryKey(id: number | string): QueryKey {
  return [MAP_LAYERS_LIST_QUERY_KEY, id.toString()] as const;
}

export const useCreateMapLayer = () => {
  const networkHeaders = useNetworkHeaders();
  const [createLayer] = useMutation(createMapLayer(networkHeaders));
  return { createLayer };
};

export const useUpdateMapLayer = () => {
  const networkHeaders = useNetworkHeaders();
  const [updateLayer] = useMutation(updateMapLayer(networkHeaders));
  return { updateLayer };
};

export const useReactivateMapLayer = () => {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const [reactivateLayer] = useMutation(reactivateMapLayer(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries(MAP_LAYERS_LIST_QUERY_KEY),
  });
  return { reactivateLayer };
};

export const useDeactivateMapLayer = () => {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const [deactivateLayer] = useMutation(deactivateMapLayer(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries(MAP_LAYERS_LIST_QUERY_KEY),
  });
  return { deactivateLayer };
};
